import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useTheme } from "@/context/ThemeContext";
import { convertText } from "@/utils/langConvert";
import { track } from "@/utils/track";
import { DOWNLOAD_PAGE_PATH } from "@/config/download";
import Sidebar from "./components/Sidebar";
import { cardDataMap, funTools } from "./data";
import { experienceModulePath } from "./routes";

interface ExperienceShellProps {
  activeId: string;
  children: ReactNode;
  contentKey: string;
  contentMode?: "default" | "tarot";
  activeFunToolId?: string | null;
}

export default function ExperienceShell({
  activeId,
  children,
  contentKey,
  contentMode = "default",
  activeFunToolId = null,
}: ExperienceShellProps) {
  const { effectiveTheme } = useTheme();
  const { i18n } = useTranslation();
  const trd = (text: string) => convertText(text, i18n.language);
  const isLight = effectiveTheme === "light";
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const currentData = cardDataMap[activeId] || cardDataMap.bazi;
  const activeFunTool = activeFunToolId
    ? funTools.find((tool) => tool.id === activeFunToolId)
    : null;
  const pageTitle = trd(activeId === "fun" && activeFunTool ? activeFunTool.name : currentData.title);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.search]);

  const handleSelectMenu = (id: string) => {
    navigate(experienceModulePath(id));
  };

  return (
    <div className={`h-screen h-[100dvh] w-full flex overflow-hidden ${isLight ? "bg-[#f5f5f7]" : "bg-cosmic-950"}`}>
      <div className="hidden md:block">
        <Sidebar
          activeId={activeId}
          onSelect={handleSelectMenu}
          isLight={isLight}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed((value) => !value)}
        />
      </div>

      <AnimatePresence>
        {mobileDrawerOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="md:hidden fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
              onClick={() => setMobileDrawerOpen(false)}
            />
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="md:hidden fixed left-0 top-0 bottom-0 z-50"
            >
              <Sidebar
                activeId={activeId}
                onSelect={handleSelectMenu}
                isLight={isLight}
                collapsed={false}
                onToggleCollapse={() => {}}
                isMobileDrawer
                onCloseDrawer={() => setMobileDrawerOpen(false)}
              />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <main className="flex-1 flex flex-col min-w-0 min-h-0">
        <header className={`md:hidden h-14 flex items-center justify-between px-4 border-b shrink-0 ${isLight ? "border-[#e5e5ea] bg-white" : "border-white/10 bg-cosmic-900"}`}>
          <button
            onClick={() => setMobileDrawerOpen(true)}
            className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${isLight ? "text-[#1c1c1e] hover:bg-[#f5f5f7]" : "text-white hover:bg-white/5"}`}
          >
            <i className="ri-menu-line text-lg" />
          </button>
          <span className={`absolute left-1/2 -translate-x-1/2 text-sm font-medium ${isLight ? "text-[#1c1c1e]" : "text-white"}`}>
            {pageTitle}
          </span>
          <Link
            to={DOWNLOAD_PAGE_PATH}
            onClick={() => track("download_click", { toolId: "experience-header" })}
            className={`text-xs px-3 py-1.5 rounded-full transition-colors cursor-pointer whitespace-nowrap ${isLight ? "bg-[#4a4a4c] text-white hover:bg-[#3a3a3c]" : "bg-gold-500 text-cosmic-950 hover:bg-gold-400"}`}
          >
            {trd("下载 APP")}
          </Link>
        </header>

        <header className={`hidden md:flex h-14 items-center px-6 border-b shrink-0 ${isLight ? "border-[#e5e5ea] bg-white" : "border-white/10 bg-cosmic-900"}`}>
          <div className={`flex items-center gap-2 text-sm ${isLight ? "text-[#6e6e73]" : "text-silver-500"}`}>
            <Link to="/" className={`transition-colors flex items-center gap-1 ${isLight ? "hover:text-[#1c1c1e]" : "hover:text-white"}`}>
              <i className="ri-arrow-left-line" />
              <span>{trd("返回首页")}</span>
            </Link>
            <span className={isLight ? "text-[#c5c5c7]" : "text-silver-600"}>/</span>
            {activeId === "fun" && activeFunTool ? (
              <>
                <Link to="/experience/fun" className={`font-medium transition-colors ${isLight ? "hover:text-[#1c1c1e]" : "hover:text-white"}`}>
                  {trd(currentData.title)}
                </Link>
                <span className={isLight ? "text-[#c5c5c7]" : "text-silver-600"}>/</span>
              </>
            ) : null}
            <span className={`font-medium ${isLight ? "text-[#1c1c1e]" : "text-white"}`}>{pageTitle}</span>
          </div>
        </header>

        <div className="flex-1 flex flex-col px-0 py-2 md:p-6 overflow-y-auto min-h-0">
          <div className={contentMode === "tarot" ? "flex-1 min-h-0" : "flex-1 min-h-full flex flex-col"}>
            <AnimatePresence mode="sync">
              <motion.div
                key={contentKey}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.28, ease: "easeOut" }}
                className="contents"
              >
                {children}
              </motion.div>
            </AnimatePresence>
          </div>

          <div className={`mt-6 py-4 border-t ${isLight ? "border-[#e5e5ea]" : "border-white/10"}`}>
            <p className={`text-xs text-center leading-relaxed max-w-2xl mx-auto mb-2 ${isLight ? "text-[#6e6e73]/50" : "text-silver-400/80"}`}>
              {trd("内容资讯仅供娱乐使用，不等于专业测评，不代表价值评判，无任何现实指导意义，仅供娱乐参考")}
            </p>
            <div className="flex justify-center gap-5">
              <Link to="/privacy" className={`text-xs transition-colors duration-150 ${isLight ? "text-[#6e6e73]/60 hover:text-[#1c1c1e]" : "text-silver-400/80 hover:text-white"}`}>
                {trd("隐私政策")}
              </Link>
              <span className={isLight ? "text-[#6e6e73]/30" : "text-silver-400/50"}>·</span>
              <Link to="/contact" className={`text-xs transition-colors duration-150 ${isLight ? "text-[#6e6e73]/60 hover:text-[#1c1c1e]" : "text-silver-400/80 hover:text-white"}`}>
                {trd("联系我们")}
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
